import React, { useEffect, useMemo, useState } from 'react';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import StatusBanner from '../../components/ui/StatusBanner';
import {
  EmployeeDashboardGrid,
  EmployeeDetailList,
  EmployeeSummaryCard,
  EmployeeWorkspaceEmptyState,
  EmployeeWorkspaceSection
} from '../../components/workforce/EmployeeWorkspace';
import { apiRequest } from '../../services/apiClient';

const EMPTY_ASYNC = Object.freeze({ status: 'idle', items: [], error: '' });

function normalizeCode(value) {
  return String(value || '').trim().toUpperCase();
}

export function filterManagerAllocations(items, managerId, locationCode, companyId = null) {
  const expectedManagerId = Number(managerId);
  const expectedLocation = normalizeCode(locationCode);
  const expectedCompanyId = Number(companyId);
  return (Array.isArray(items) ? items : []).filter(item => (
    Number(item?.managerId) === expectedManagerId
    && (!expectedLocation || normalizeCode(item?.locationCode) === expectedLocation)
    && (!(expectedCompanyId > 0) || Number(item?.companyId) === expectedCompanyId)
  ));
}

export function filterLocationGoals(items, locationCode, companyId = null) {
  const expectedLocation = normalizeCode(locationCode);
  const expectedCompanyId = Number(companyId);
  if (!expectedLocation) return [];
  return (Array.isArray(items) ? items : []).filter(item => (
    normalizeCode(item?.locationCode) === expectedLocation
    && (!(expectedCompanyId > 0) || Number(item?.companyId) === expectedCompanyId)
  ));
}

function formatPeriod(item) {
  if (item?.goalPeriodType === 'yearly') return String(item.goalYear || 'Yearly period');
  if (item?.goalPeriodType === 'quarterly') return item.goalQuarter && item.goalYear ? `Q${item.goalQuarter} ${item.goalYear}` : 'Quarterly period';
  if (item?.goalPeriodType === 'weekly') return item.goalWeek && item.goalYear ? `Week ${item.goalWeek}, ${item.goalYear}` : 'Weekly period';
  if (item?.goalMonth && item?.goalYear) return `${item.goalYear}-${String(item.goalMonth).padStart(2, '0')}`;
  return item?.periodStart ? String(item.periodStart).slice(0, 10) : 'Period unavailable';
}

function formatValue(value, unit) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'Value unavailable';
  return unit === 'percentage' ? `${numeric}%` : numeric.toLocaleString();
}

function AsyncRecords({ state, emptyTitle, emptyBody, children }) {
  if (state.status === 'loading') return <LoadingSkeleton variant="paragraph" lines={4} />;
  if (state.status === 'error') return <StatusBanner kind="error" message={state.error} />;
  if (!state.items.length) return <EmptyState compact icon="·" headline={emptyTitle} body={emptyBody} />;
  return children;
}

export default function EmployeeGoalsWorkspace({ profile, manager = null }) {
  const managerId = Number(profile?.managerId);
  const companyId = Number(profile?.companyId);
  const isManagerBacked = Number.isFinite(managerId) && managerId > 0;
  const locationCode = normalizeCode(manager?.locationCode);
  const [allocations, setAllocations] = useState(isManagerBacked ? { ...EMPTY_ASYNC, status: 'loading' } : EMPTY_ASYNC);
  const [locationGoals, setLocationGoals] = useState(isManagerBacked && locationCode ? { ...EMPTY_ASYNC, status: 'loading' } : EMPTY_ASYNC);

  useEffect(() => {
    if (!isManagerBacked) {
      setAllocations(EMPTY_ASYNC);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setAllocations({ ...EMPTY_ASYNC, status: 'loading' });
    apiRequest(`/api/goals/manager/${managerId}`, { signal: controller.signal })
      .then(items => {
        if (!cancelled) setAllocations({ status: 'loaded', items: filterManagerAllocations(items, managerId, locationCode, companyId), error: '' });
      })
      .catch(error => {
        if (!cancelled) setAllocations({ status: 'error', items: [], error: error?.message || 'Unable to load manager-backed allocations.' });
      });
    return () => { cancelled = true; controller.abort(); };
  }, [companyId, isManagerBacked, locationCode, managerId]);

  useEffect(() => {
    if (!isManagerBacked || !locationCode) {
      setLocationGoals(EMPTY_ASYNC);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setLocationGoals({ ...EMPTY_ASYNC, status: 'loading' });
    apiRequest(`/api/goals/location?locationCode=${encodeURIComponent(locationCode)}`, { signal: controller.signal })
      .then(items => {
        if (!cancelled) setLocationGoals({ status: 'loaded', items: filterLocationGoals(items, locationCode, companyId), error: '' });
      })
      .catch(error => {
        if (!cancelled) setLocationGoals({ status: 'error', items: [], error: error?.message || 'Unable to load contextual location goals.' });
      });
    return () => { cancelled = true; controller.abort(); };
  }, [companyId, isManagerBacked, locationCode]);

  const reportingPeriods = useMemo(() => {
    const values = [...allocations.items, ...locationGoals.items].map(formatPeriod).filter(Boolean);
    return [...new Set(values)];
  }, [allocations.items, locationGoals.items]);

  return (
    <div className="employee-goals" data-testid="employee-goals-workspace">
      <EmployeeWorkspaceSection title="Goals overview" description="Source availability for this employee profile">
        <EmployeeDashboardGrid columns="wide">
          <EmployeeSummaryCard label="Employee goals available" value="No" detail="No employee-owned goal model exists" />
          <EmployeeSummaryCard label="Manager-backed allocations available" value={isManagerBacked ? 'Yes' : 'No'} detail={isManagerBacked ? 'Associated through the current manager record' : 'No manager record is linked'} />
          <EmployeeSummaryCard label="Location goals available" value={isManagerBacked && locationCode ? 'Yes' : 'No'} detail={locationCode ? `Context only · ${locationCode}` : 'No authorized manager location is available'} />
          <EmployeeSummaryCard label="Reporting period" value={reportingPeriods.length ? reportingPeriods.join(', ') : 'Not available'} detail="Derived only from loaded contextual records" />
        </EmployeeDashboardGrid>
        <p className="employee-workspace__scope-note" data-testid="employee-goals-scope-disclosure">
          This repository models goals at location scope and allocations at manager scope. Neither source is an employee-owned goal, employee target, employee actual, or employee progress record.
        </p>
      </EmployeeWorkspaceSection>

      <EmployeeWorkspaceSection title="Employee goals" description="Employee-owned records only">
        <EmployeeWorkspaceEmptyState title="No employee goals available" description="The current data model does not contain goals owned by an employee profile. Manager and location records are kept separate below as context." release="Not modeled" />
      </EmployeeWorkspaceSection>

      <EmployeeWorkspaceSection title="Manager-backed allocations" description="Operational allocations owned by the linked manager record">
        {isManagerBacked ? (
          <AsyncRecords state={allocations} emptyTitle="No manager-backed allocations available" emptyBody="No authorized allocations are recorded for this employee's linked manager and location.">
            <ul className="employee-performance__records" aria-label="Manager-backed allocations">
              {allocations.items.map(item => (
                <li key={item.id}>
                  <strong>{item.kpiLabel || item.kpiKey || 'KPI allocation'}</strong>
                  <span>Manager allocation: {formatValue(item.allocatedValue, item.unit)} · {formatPeriod(item)}</span>
                  <span>Manager: {item.managerName || `Manager #${managerId}`} · Location: {item.locationCode || 'Unavailable'} · Locked allocation</span>
                </li>
              ))}
            </ul>
          </AsyncRecords>
        ) : <EmployeeWorkspaceEmptyState title="No manager-backed goal context" description="This standalone employee profile has no linked manager allocation identity." />}
      </EmployeeWorkspaceSection>

      <EmployeeWorkspaceSection title="Location goals" description="Authorized location context only — not employee ownership">
        {isManagerBacked && locationCode ? (
          <AsyncRecords state={locationGoals} emptyTitle="No contextual location goals available" emptyBody={`No authorized location goals are recorded for ${locationCode}.`}>
            <ul className="employee-performance__records" aria-label="Contextual location goals">
              {locationGoals.items.map(item => (
                <li key={item.id}>
                  <strong>{item.label || item.kpiLabel || item.kpiKey || 'Location KPI goal'}</strong>
                  <span>Location goal: {formatValue(item.goalValue, item.unit)} · {formatPeriod(item)}</span>
                  <span>Location: {item.locationCode} · Status: {item.goalStatus || 'Unavailable'} · {item.isLocked ? 'Locked' : 'Not locked'}</span>
                </li>
              ))}
            </ul>
          </AsyncRecords>
        ) : <EmployeeWorkspaceEmptyState title="Location goal context unavailable" description="A linked, authorized manager location is required before location goals can be shown as context." />}
      </EmployeeWorkspaceSection>

      <EmployeeDashboardGrid>
        <EmployeeWorkspaceSection title="Goal progress" description="Employee-level target and actual boundary">
          <EmployeeWorkspaceEmptyState title="Employee goal progress is not available" description="No employee target and employee actual can be connected at the same scope. Manager and location actuals are not used to calculate employee progress." />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Goal history" description="Employee-owned history only">
          <EmployeeWorkspaceEmptyState title="No employee goal history available" description="Location goal history and manager allocation history are not employee goal history." />
        </EmployeeWorkspaceSection>
      </EmployeeDashboardGrid>

      <EmployeeWorkspaceSection title="Future employee goal model" description="Required before employee-owned goals can be supported">
        <EmployeeDetailList items={[
          { label: 'Ownership', value: 'Employee profile linkage with tenant and location scope' },
          { label: 'Goal contract', value: 'KPI, target, status, period, and optional due date' },
          { label: 'Progress contract', value: 'Employee-scoped actuals linked to the same goal and period' },
          { label: 'Governance', value: 'Permissions, approvals, comments, immutable history, and audit records' }
        ]} />
      </EmployeeWorkspaceSection>

      <EmployeeWorkspaceSection title="AI goal assistance" description="Future only — no AI processing is active">
        <EmployeeWorkspaceEmptyState title="AI goal assistance is planned" description="No goals, progress, recommendations, or coaching suggestions are generated. Governed assistance requires an authoritative employee goal model first." release="Future release" />
      </EmployeeWorkspaceSection>
    </div>
  );
}
